import React, { useState } from 'react';
import { Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    workEmail: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.fullName || !formData.company || !formData.workEmail || !formData.message) {
      toast({
        title: 'Required Fields',
        description: 'All fields are required.',
        variant: 'destructive'
      });
      return;
    }

    if (!validateEmail(formData.workEmail)) {
      toast({
        title: 'Invalid Email',
        description: 'Valid email address required.',
        variant: 'destructive'
      });
      return;
    }

    if (formData.message.length < 10) {
      toast({
        title: 'Message Required',
        description: 'Message must be at least 10 characters.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);

      if (response.data.success) {
        setIsSubmitted(true);
        setFormData({
          fullName: '',
          company: '',
          workEmail: '',
          message: ''
        });

        toast({
          title: 'Submitted',
          description: response.data.message
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      
      const errorMessage = error.response?.data?.detail || 
        'Unable to process submission. Contact info@brxresource.com directly.';
      
      toast({
        title: 'Submission Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">Contact</h1>
            <p className="text-xl text-gray-600">
              For vendor-to-vendor engagement inquiries.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Contact Channels</h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <Mail size={24} className="text-blue-900 mr-4 mt-1" />
                <div>
                  <a href="mailto:info@brxresource.com" className="text-lg font-medium text-gray-900 hover:text-blue-900 transition-colors">
                    info@brxresource.com
                  </a>
                  <p className="text-sm text-gray-600 mt-1">General & Partnerships</p>
                </div>
              </div>
              <div className="flex items-start">
                <Mail size={24} className="text-blue-900 mr-4 mt-1" />
                <div>
                  <a href="mailto:finance@brxresource.com" className="text-lg font-medium text-gray-900 hover:text-blue-900 transition-colors">
                    finance@brxresource.com
                  </a>
                  <p className="text-sm text-gray-600 mt-1">Billing & Finance</p>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin size={24} className="text-blue-900 mr-4 mt-1" />
                <div>
                  <p className="text-lg font-medium text-gray-900">Location</p>
                  <p className="text-sm text-gray-600 mt-1">Noida, Uttar Pradesh, India</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 p-8 rounded-lg border border-gray-200">
            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle size={64} className="text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">Received</h3>
                <p className="text-gray-600 mb-6">We will review and respond as appropriate.</p>
                <Button onClick={() => setIsSubmitted(false)} variant="outline">
                  Submit Another Inquiry
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="fullName" className="text-gray-900">Full Name</Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    className="mt-2"
                    maxLength={100}
                  />
                </div>

                <div>
                  <Label htmlFor="company" className="text-gray-900">Company</Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    className="mt-2"
                    maxLength={100}
                  />
                </div>

                <div>
                  <Label htmlFor="workEmail" className="text-gray-900">Work Email</Label>
                  <Input
                    id="workEmail"
                    name="workEmail"
                    type="email"
                    value={formData.workEmail}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-gray-900">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    rows={5}
                    className="mt-2"
                    maxLength={2000}
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting} 
                  className="w-full bg-blue-900 hover:bg-blue-800"
                  aria-label="Submit contact form"
                >
                  {isSubmitting ? (
                    'Processing...'
                  ) : (
                    <>
                      Submit
                      <Send size={18} className="ml-2" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>

        {/* Notice */}
        <div className="max-w-7xl mx-auto mt-12">
          <div className="bg-blue-50 border border-blue-200 rounded p-4 flex items-start">
            <AlertCircle size={20} className="text-blue-900 mr-3 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700">
              All inquiries are subject to review and response as appropriate. Submission does not constitute acceptance of any terms or commitment to provide services.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;